import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { citySeoFromApi } from "../constants/citySeo";

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/public/city-seo/${citySlug}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || "城市页面不存在");
        }
        if (!cancelled) {
          setCity(data);
        }

        const listRes = await fetch(
          `/api/public/city-seo?page=1&page_size=12&province=${encodeURIComponent(data.province)}`
        );
        const listData = await listRes.json();
        if (!cancelled && listRes.ok) {
          setRelated(
            (listData.items || []).filter((item) => item.slug !== data.slug).slice(0, 8)
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "加载失败");
          setCity(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [citySlug]);

  useSeo(
    city
      ? citySeoFromApi(city)
      : { title: "城市服务", path: `/city/${citySlug || ""}`, noindex: true }
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-gray-400">加载中...</div>
        <Footer />
      </div>
    );
  }

  if (error || !city) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-gray-500">
          <p>{error || "页面不存在"}</p>
          <button
            onClick={() => navigate("/city")}
            className="px-4 py-2 rounded-full bg-pink-500 text-white text-sm"
          >
            查看全部城市
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const locationLabel =
    city.level === "district"
      ? `${city.short_name}（${city.city}）`
      : city.level === "province"
        ? `${city.short_name}全省`
        : city.short_name;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-6">
          <header className="space-y-3">
            <p className="text-sm text-pink-500 font-medium">同城婚恋 · 脱单 · 咨询</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              {locationLabel}找对象 · 脱单相亲 · 婚恋咨询
            </h1>
            <p className="text-gray-600 leading-relaxed">
              在{locationLabel}寻找合适的结婚对象或认真脱单？AI婚恋平台提供同城智能匹配与专业咨询师免费陪伴。
              支持婚恋咨询、婚姻咨询与婚姻修复辅导，区别于传统相亲介绍，咨询师从相识、交往到结婚全程陪伴，帮助你在
              {city.local_hint}建立以结婚为目的的健康关系。
            </p>
          </header>

          <section className="grid md:grid-cols-3 gap-4">
            {[
              {
                title: "找对象 · 脱单",
                text: `按${locationLabel}同城条件智能推荐合适对象，支持相亲征婚与严肃婚恋。`,
              },
              {
                title: "咨询师陪伴",
                text: "前期免费一对一陪伴，交往全过程辅导，不是简单介绍一次。",
              },
              {
                title: "婚恋 · 婚姻咨询",
                text: "恋爱指导、婚前辅导、婚姻咨询与婚姻修复，关系问题可预约咨询师。",
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl bg-rose-50 p-4">
                <h2 className="font-bold text-pink-600 mb-2">{item.title}</h2>
                <p className="text-sm text-gray-600">{item.text}</p>
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold text-gray-800">{locationLabel}用户常见问题</h2>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="rounded-xl border border-pink-100 p-4">
                <h3 className="font-semibold text-gray-800 mb-1">
                  在{locationLabel}怎么开始找对象？
                </h3>
                <p>
                  免费注册并完善资料，完成 AI 测评后即可查看同城推荐对象，并可联系咨询师获得免费陪伴。
                </p>
              </div>
              <div className="rounded-xl border border-pink-100 p-4">
                <h3 className="font-semibold text-gray-800 mb-1">
                  和其他婚恋网站有什么不同？
                </h3>
                <p>
                  平台强调「有人陪伴再恋爱」：专业咨询师长期免费陪伴，帮助你在交往过程中更稳妥地走向婚姻。
                </p>
              </div>
            </div>
          </section>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to="/register"
              className="px-6 py-3 bg-pink-500 text-white rounded-full text-sm font-medium hover:bg-pink-600 transition"
            >
              免费注册 · {locationLabel}找对象
            </Link>
            <Link
              to={city.discover_path}
              className="px-6 py-3 border border-pink-400 text-pink-600 rounded-full text-sm hover:bg-pink-50 transition"
            >
              查看{locationLabel}同城会员
            </Link>
            <Link
              to="/consultants"
              className="px-6 py-3 border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-gray-50 transition"
            >
              了解咨询师服务
            </Link>
          </div>
        </article>

        {related.length > 0 && (
          <section className="max-w-3xl mx-auto mt-8">
            <h2 className="text-sm text-gray-500 mb-3">同城更多区域</h2>
            <div className="flex flex-wrap gap-2">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/city/${item.slug}`}
                  className="px-3 py-1.5 bg-white rounded-full text-sm text-gray-600 border border-pink-100 hover:border-pink-300 hover:text-pink-600 transition"
                >
                  {item.short_name}婚恋交友
                </Link>
              ))}
              <Link to="/city" className="px-3 py-1.5 text-sm text-pink-500 hover:underline">
                全部城市 →
              </Link>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
